/**
 * Lot interaction for the SVG composed overlay.
 *
 * Manages hover tracking, click handling, and drag detection on the SVG root via
 * event delegation. All state is internal; the caller receives { bind, unbind }
 * to attach and detach listeners from the SVG element.
 *
 * @module mapLibrary/svgLotInteraction
 */

import { isDragGesture } from './overlayUtils';

/**
 * Walks up from a DOM node to find the nearest ancestor-or-self that has an id,
 * stopping before (and not returning) the SVG root element.
 *
 * Lot polygons in the composed SVG are typically <g id="..."> containers whose
 * child <path> elements have no id. event.target gives the leaf <path>;
 * this function climbs to the lot group.
 *
 * @param {Element} target
 * @param {Element} root - Boundary element (svgElement); never returned
 * @returns {Element|null}
 */
function findLotElement(target, root) {
  let node = target;
  while (node && node !== root) {
    if (node.id) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Wires lot hover and click interactions onto an SVG element.
 *
 * @param {SVGElement} svgElement
 * @param {object} handlers
 * @param {Function} [handlers.onLotClick]  - Called with { lotId } on click
 * @param {Function} [handlers.onLotHover]  - Called with { lotId } when the active lot changes
 * @param {Function} [handlers.onLotEnter]  - Called with { target, svgElement } on every target change
 * @param {Function} [handlers.onLotLeave]  - Called with { svgElement } when pointer leaves the SVG
 * @returns {{ bind: Function, unbind: Function }}
 */
function createLotInteraction(svgElement, { onLotClick, onLotHover, onLotEnter, onLotLeave }) {
  const state = {
    currentLotId:   null,
    pointerDownPos: null,
    currentTarget:  null,
    rafId:          null,
  };

  function handleMouseDown(event) {
    state.pointerDownPos = { x: event.clientX, y: event.clientY };
  }

  function handleClick(event) {
    if (!onLotClick) {
      return;
    }
    if (isDragGesture(state.pointerDownPos, event.clientX, event.clientY)) {
      return;
    }
    const lotElement = findLotElement(event.target, svgElement);
    if (lotElement) {
      onLotClick({ lotId: lotElement.id });
    }
  }

  function handleMouseMove(event) {
    if (state.rafId) {
      return;
    }
    state.rafId = requestAnimationFrame(() => {
      state.rafId = null;
      const lotElement = findLotElement(event.target, svgElement);
      const newLotId   = lotElement ? lotElement.id : null;

      if (event.target !== state.currentTarget) {
        state.currentTarget = event.target;
        onLotEnter?.({ target: event.target, svgElement });
      }

      if (newLotId === state.currentLotId) {
        return;
      }
      if (newLotId && onLotHover) {
        onLotHover({ lotId: newLotId });
      }
      state.currentLotId = newLotId;
    });
  }

  function handleMouseLeave() {
    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }
    state.currentTarget = null;
    onLotLeave?.({ svgElement });
    state.currentLotId = null;
  }

  function bind() {
    svgElement.addEventListener('mousedown',  handleMouseDown);
    svgElement.addEventListener('click',      handleClick);
    svgElement.addEventListener('mouseleave', handleMouseLeave);
    svgElement.addEventListener('mousemove',  handleMouseMove);
  }

  function unbind() {
    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }
    svgElement.removeEventListener('mousedown',  handleMouseDown);
    svgElement.removeEventListener('click',      handleClick);
    svgElement.removeEventListener('mouseleave', handleMouseLeave);
    svgElement.removeEventListener('mousemove',  handleMouseMove);
  }

  return { bind, unbind };
}

export default createLotInteraction;
